import type { HostInviteService, HostDirectoryService, IncomingInvite, Result } from './types.js';
import type { BroadcastService } from './broadcast-service.js';
import { parseHtsUri } from '../validation/uri-validation.js';

export class InviteService implements HostInviteService {
  private inviteHandlers = new Set<(invite: IncomingInvite) => void>();
  private pendingInvites = new Map<string, IncomingInvite>();

  constructor(
    private readonly nodeId: string,
    private readonly directory: HostDirectoryService,
    private readonly broadcast: BroadcastService
  ) {}

  async sendInvite(request: { targetNodeId: string; channelUri: string; metadata?: unknown }): Promise<Result<void>> {
    const peer = this.directory.getPeer(request.targetNodeId);
    if (!peer || peer.status === 'offline') {
      return {
        ok: false,
        error: {
          type: 'PEER_UNREACHABLE',
          code: 'ERR_PEER_UNREACHABLE',
          message: `Peer '${request.targetNodeId}' is offline or not found in directory.`,
        },
      };
    }

    try {
      parseHtsUri(request.channelUri);

      const invite: IncomingInvite = {
        inviteId: crypto.randomUUID(),
        senderNodeId: this.nodeId,
        channelUri: request.channelUri,
        metadata: request.metadata,
        timestamp: Date.now(),
      };

      // Publish invite envelope to target peer's inbox or broadcast
      const publishResult = await this.broadcast.publish(request.channelUri, {
        type: 'INVITE',
        invite,
      });

      if (!publishResult.ok) {
        return publishResult;
      }

      return { ok: true, value: undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        ok: false,
        error: {
          type: 'INVALID_URI',
          code: 'ERR_INVALID_URI',
          message,
        },
      };
    }
  }

  receiveIncomingInvite(invite: IncomingInvite): void {
    this.pendingInvites.set(invite.inviteId, invite);
    for (const handler of this.inviteHandlers) {
      handler(invite);
    }
  }

  onInviteReceived(handler: (invite: IncomingInvite) => void): () => void {
    this.inviteHandlers.add(handler);
    return () => {
      this.inviteHandlers.delete(handler);
    };
  }

  async acceptInvite(inviteId: string): Promise<Result<{ channelUri: string }>> {
    const invite = this.pendingInvites.get(inviteId);
    if (!invite) {
      return {
        ok: false,
        error: {
          type: 'BRIDGE_ERROR',
          code: 'ERR_INVITE_NOT_FOUND',
          message: `Invite '${inviteId}' was not found or already processed.`,
        },
      };
    }

    this.pendingInvites.delete(inviteId);
    return { ok: true, value: { channelUri: invite.channelUri } };
  }

  async rejectInvite(inviteId: string): Promise<Result<void>> {
    const invite = this.pendingInvites.get(inviteId);
    if (!invite) {
      return {
        ok: false,
        error: {
          type: 'BRIDGE_ERROR',
          code: 'ERR_INVITE_NOT_FOUND',
          message: `Invite '${inviteId}' was not found or already processed.`,
        },
      };
    }

    this.pendingInvites.delete(inviteId);
    return { ok: true, value: undefined };
  }
}
